import { Fragment, useEffect, useMemo, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { TaskCaseDetailsBreadcrumb } from '../../components/tasks/TaskCaseDetailsBreadcrumb';
import { useTaskCaseDetailsReturnState } from '../../hooks/useTaskCaseDetailsReturnState';
import { CriminalCoverNoteModal } from '../../components/coverNote/CriminalCoverNoteModal';
import CommonCoverNoteModal from '../../components/wizard/CommonCoverNoteModal';
import { getCriminalCoverNote } from '../../services/criminalCaseRequests';
import { getCommonCoverNote } from '../../services/coverNoteRequests';
import { isCaseRequestUuid } from '../task-case/taskCaseUpdateHelpers';
import { printCoverNotePdf } from '../../utils/coverNotePrint';
import { coverNoteQrImageSrc } from '../../utils/coverNoteQr';
import { setFlashToast } from '../../utils/flashToast';
import type { TaskFmlEvent, TaskWorkflowStep } from '../../data/taskCaseDetails';
import { useTaskCaseDetailsData } from '../../hooks/useTaskCaseDetailsData';
// EM-1: tasks are "In Progress" immediately after allocation — manual Start is no longer required.
// import { startUserTask } from '../../services/tasks';
import { validateSubmitWorkflowTask } from '../../services/taskSubmission';
import {
  areTaskCaseTabsEditable,
  isInProgressTaskStatus,
  isOnHoldTaskStatus,
} from '../../utils/taskCaseDetailMappers';
// import { isNotStartedTaskStatus } from '../../utils/taskCaseDetailMappers';
import TaskCaseCommunicationsTab from '../task-case/TaskCaseCommunicationsTab';
import TaskCaseDocumentsTab from '../task-case/TaskCaseDocumentsTab';
import TaskCaseMinutesTab from '../task-case/TaskCaseMinutesTab';
import TaskCaseInvolvementTab from '../task-case/TaskCaseInvolvementTab';
import TaskCaseSubTasksTab from '../task-case/TaskCaseSubTasksTab';
import TaskCaseUpdateTabRouter from '../task-case/TaskCaseUpdateTabRouter';
import TaskCaseDraftLetterTab from '../task-case/TaskCaseDraftLetterTab';
import TaskCaseAuditLogTab from '../task-case/TaskCaseAuditLogTab';
import ViewRequestCaseDetailsRouter from '../view-request/ViewRequestCaseDetailsRouter';
import CriminalAppellateCoverNoteModal from '../wizard/criminalAppellate/CriminalAppellateCoverNoteModal';
import RightToInformationCoverNoteModal from '../wizard/rightToInformation/RightToInformationCoverNoteModal';
import {
  getRightToInformationCoverNote,
  rightToInformationDraftToCoverNoteFallback,
} from '../wizard/rightToInformation/rightToInformationCoverNote';
import { usesCommonCoverNote, usesCriminalAppellateCoverNote } from '../../utils/wizardCategoryRoute';
import { ForwardTaskModal } from '../../components/tasks/ForwardTaskModal';
import { SubmitTaskModal } from '../../components/tasks/SubmitTaskModal';
import { SelectUserForTaskClosureModal } from '../../components/tasks/SelectUserForTaskClosureModal';
    // UAT-45: Reject action removed — rejections now route through closure workflow.
    // import { RejectTaskModal } from '../../components/tasks/RejectTaskModal';
import { RevertTaskModal } from '../../components/tasks/RevertTaskModal';
import { getActiveAppUserRole } from '../../services/activeAppUser';
import { usePermission, useRole } from '../../rbac/hooks';
import {
  canUseCaseWorkflowActions,
  isAllocationOfficerUser,
  resolveSubTasksTabReadOnly,
} from '../task-case/taskCaseSubTaskAccess';
import {
  canUpdateTaskCaseDetails,
  readTaskCaseDetailsTabFromSearchParams,
} from '../task-case/taskCaseDetailsAccess';
import { getWorkflowTaskSubsidiaryStatus } from '../../services/caseRequestSubsidiaryTasks';
import { validateClosureApproverClick, type ExistingClosureAssignment } from '../../services/taskClosure';
import {
  translateCustodyStatus,
  translateTaskStatus,
} from '../../utils/translateKnownLabel';

/** Workflow diagram + Cover Note actions — domain labels stay in English. */
const WORKFLOW_LEGEND_COMPLETED = 'Completed';
const WORKFLOW_LEGEND_CURRENT = 'Current';
const WORKFLOW_LEGEND_PENDING = 'Pending';

type TabId = 'cd' | 'cu' | 'docs' | 'comm' | 'min' | 'ltr' | 'st' | 'fml' | 'al' | 'inv';
type WfaAction = '' | 'forward' | 'revert' | 'reject' | 'closure';

function isExcludedWorkflowActionStage(stageName: string): boolean {
  const s = (stageName ?? '').trim().toLowerCase();
  if (!s) return false;
  return s === 'allocation officer assignment'.toLowerCase() || s === 'return to allocation officer'.toLowerCase();
}

function taskTypeKey(taskType: string | undefined): 'regular' | 'review' | 'subsidiary' | 'unknown' {
  const s = (taskType ?? '').trim().toLowerCase();
  if (s === 'regular') return 'regular';
  if (s === 'review') return 'review';
  if (s === 'subsidiary') return 'subsidiary';
  return s ? 'unknown' : 'unknown';
}

function fmlEvClass(e: TaskFmlEvent): string {
  return e === 'qrin' ? 'ev-qrin' : 'ev-qrout';
}

function renderWorkflowStep(s: TaskWorkflowStep, isLast: boolean) {
  const title = s.title;
  const line1 = s.line1;
  const line2 = s.line2;

  const dot =
    s.kind === 'done' ? (
      <div className="wf-dot done" />
    ) : s.kind === 'curr' ? (
      <div className="wf-dot curr">
        <div className="wf-di" />
      </div>
    ) : (
      <div className="wf-dot pend" />
    );

  const body =
    s.kind === 'pend' ? (
      <>
        <div className="wf-sn p">{title}</div>
        <div className="wf-nt" style={{ color: 'var(--t3)' }}>
          {line2}
        </div>
      </>
    ) : (
      <>
        <div className="wf-sn">{title}</div>
        <div className="wf-dt">{line1}</div>
        <div className="wf-nt">{line2}</div>
      </>
    );

  return (
    <div className="wf-step">
      <div className="wf-l">
        {dot}
        {!isLast ? <div className="wf-conn" /> : null}
      </div>
      <div className="wf-info" style={isLast ? { paddingBottom: 0 } : undefined}>
        {body}
      </div>
    </div>
  );
}

function taskDetailsPath(caseRequestId: string, userTaskId?: string): string {
  const base = `/tasks/${encodeURIComponent(caseRequestId)}`;
  const ut = (userTaskId ?? '').trim();
  if (!ut || ut === caseRequestId) return base;
  return `${base}?userTaskId=${encodeURIComponent(ut)}`;
}

export default function TaskCaseDetailsPage() {
  const { t } = useTranslation();
  const { taskId: caseRequestIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const nav = useNavigate();
  const breadcrumbReturn = useTaskCaseDetailsReturnState();
  const viewMode = searchParams.get('mode') === 'view';

  const caseRequestId = decodeURIComponent(caseRequestIdParam ?? '').trim();
  const userTaskId = (searchParams.get('userTaskId') ?? '').trim();

  const initialTab = readTaskCaseDetailsTabFromSearchParams(searchParams, viewMode);
  const [tab, setTab] = useState<TabId>(initialTab);
  const activeTabForLoad: TabId = viewMode && tab === 'cu' ? 'cd' : tab;

  const {
    loading,
    error,
    detail,
    reload,
    resolvedCaseRequestId,
    flow,
    viewPayload,
    viewPayloadLoading,
    caseDetailsError,
  } = useTaskCaseDetailsData(caseRequestId, userTaskId, activeTabForLoad);
  const { has, loading: permissionsLoading } = usePermission();
  const { roleName, roleCode } = useRole();
  // Forward always drops the user on the shared /tasks screen's "Linked" tab when that's
  // where they're headed — other destinations (dedicated role screens, dashboards, etc.)
  // don't have that tab concept, so just return there as-is.
  const forwardReturnPath =
    breadcrumbReturn.returnTo === '/tasks' ? '/tasks?mainTab=active&subTab=linked' : breadcrumbReturn.returnTo;
  const effectiveRoleName = roleName || getActiveAppUserRole();
  const allocationOfficerUser = isAllocationOfficerUser(roleCode, has, effectiveRoleName);
  const showCaseWorkflowActions = canUseCaseWorkflowActions(
    effectiveRoleName,
    has('ASSIGN_SUB_TASKS'),
    roleCode,
    has,
  );
  const readOnly = viewMode || detail.variant === 'rejected';

  useEffect(() => {
    if (permissionsLoading || viewMode) return;
    if (canUpdateTaskCaseDetails(roleCode, has)) return;

    const qs = new URLSearchParams(searchParams);
    qs.set('mode', 'view');
    nav(`${location.pathname}?${qs.toString()}`, { replace: true });
  }, [permissionsLoading, viewMode, roleCode, has, searchParams, nav, location.pathname]);

  const [showCoverNote, setShowCoverNote] = useState(false);
  const [coverNoteQr, setCoverNoteQr] = useState('');
  const [coverNoteLoading, setCoverNoteLoading] = useState(false);
  const [printingCoverNote, setPrintingCoverNote] = useState(false);
  // EM-1: Start button removed — allocation sets task status to "In Progress" automatically.
  // const [starting, setStarting] = useState(false);
  const [wfa, setWfa] = useState<WfaAction>('');
  const [forwardOpen, setForwardOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitValidating, setSubmitValidating] = useState(false);
  const [closureOpen, setClosureOpen] = useState(false);
    // UAT-45: Reject action removed — rejections now route through closure workflow.
    // const [rejectOpen, setRejectOpen] = useState(false);
  const [revertOpen, setRevertOpen] = useState(false);

  const [blockingSubTasks, setBlockingSubTasks] = useState(false);
  const [blockingLoading, setBlockingLoading] = useState(false);
  const [closureExistingAssignment, setClosureExistingAssignment] = useState<
    ExistingClosureAssignment | undefined
  >();
  const [closureValidating, setClosureValidating] = useState(false);

  // EM-1: Start button removed — no longer need to detect "Not Started" for a manual start action.
  // const canStart = isNotStartedTaskStatus(detail.taskStatus);
  const isInProgress = isInProgressTaskStatus(detail.taskStatus);
  // EM-1: was only used to hide the Start button on the last workflow stage.
  // const workflowAtLastStage =
  //   detail.workflowSteps.length > 0 && detail.workflowSteps[detail.workflowSteps.length - 1]?.kind === 'curr';

  // Data Dictionary / 0002252: Workflow actions and tab edits only when status is "In Progress".
  const workflowActionsEnabled = isInProgress;
  // Pending / Assigned / Not Started / view / rejected → Case Update and other tabs stay read-only.
  const tabsReadOnly = readOnly || !areTaskCaseTabsEditable(detail.taskStatus);
  const taskStatusReadOnly = detail.variant === 'rejected' || !areTaskCaseTabsEditable(detail.taskStatus);
  const subTasksTabReadOnly = resolveSubTasksTabReadOnly({
    tabsReadOnly,
    taskStatusReadOnly,
    roleName: effectiveRoleName,
    hasAssignSubTasks: has('ASSIGN_SUB_TASKS'),
    taskType: detail.taskType,
    isReceivedViaForward: detail.isReceivedViaForward,
    roleCode,
    hasPermission: has,
  });
  const communicationsComposeEnabled = !readOnly && isInProgress;
  const communicationsHoldResumeEnabled =
    !readOnly && (isInProgress || isOnHoldTaskStatus(detail.taskStatus));

  const tabs = useMemo(
    () => [
      { id: 'cd' as TabId, label: t('tasks.details.tabCaseDetails') },
      { id: 'cu' as TabId, label: t('tasks.details.tabCaseUpdate') },
      { id: 'docs' as TabId, label: t('tasks.details.tabDocuments') },
      { id: 'comm' as TabId, label: t('tasks.details.tabCommunications') },
      { id: 'min' as TabId, label: t('tasks.details.tabCaseMinutes') },
      { id: 'ltr' as TabId, label: t('tasks.details.tabDraftLetter') },
      { id: 'st' as TabId, label: t('tasks.details.tabSubTasks') },
      { id: 'fml' as TabId, label: t('tasks.details.tabFileMovementLog') },
      { id: 'al' as TabId, label: t('tasks.details.tabAuditLog') },
      { id: 'inv' as TabId, label: t('tasks.details.tabInvolvement') }
    ],
    [t],
  );

  const visibleTabs = useMemo(
    () =>
      tabs.filter((tabItem) => {
        // 0002252: Case Update must not appear on the View screen (?mode=view).
        if (viewMode && tabItem.id === 'cu') return false;
        // Case Update is available for every known case-type flow.
        if (tabItem.id === 'cu' && flow === 'unknown') return false;
        return true;
      }),
    [tabs, viewMode, flow],
  );

  const activeTab: TabId = activeTabForLoad;

  const qrRequestId = isCaseRequestUuid(resolvedCaseRequestId)
    ? resolvedCaseRequestId
    : isCaseRequestUuid(caseRequestId)
      ? caseRequestId
      : '';

  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect */
    if (!qrRequestId || flow === 'unknown') {
      setCoverNoteQr('');
      setCoverNoteLoading(false);
      return;
    }

    let mounted = true;
    setCoverNoteLoading(true);
    setCoverNoteQr('');

    const loadQr = async () => {
      if (usesCommonCoverNote(flow) || usesCriminalAppellateCoverNote(flow)) {
        const r = await getCommonCoverNote(qrRequestId);
        if (!mounted) return;
        setCoverNoteLoading(false);
        if (!r.success) return;
        setCoverNoteQr(r.data.qr ?? '');
        return;
      }

      if (flow === 'right-to-information') {
        const r = await getRightToInformationCoverNote(qrRequestId);
        if (!mounted) return;
        setCoverNoteLoading(false);
        if (!r.success) return;
        setCoverNoteQr(r.data.qr ?? '');
        return;
      }

      const r = await getCriminalCoverNote(qrRequestId);
      if (!mounted) return;
      setCoverNoteLoading(false);
      if (!r.success) return;
      setCoverNoteQr(r.data.qr ?? '');
    };

    void loadQr();

    return () => {
      mounted = false;
    };
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [qrRequestId, flow]);

  const qrSrc = coverNoteQrImageSrc(coverNoteQr);
  const rtiCoverNoteFallback = useMemo(() => {
    if (flow !== 'right-to-information' || !viewPayload || viewPayload.flow !== 'right-to-information') {
      return undefined;
    }
    return rightToInformationDraftToCoverNoteFallback(viewPayload.data.draft, {
      fileRef: detail.fileRef,
      submittedAt: viewPayload.data.submittedAt,
      submittedDivisionName: viewPayload.data.draft.submittedDivisionName,
    });
  }, [flow, viewPayload, detail.fileRef]);

  const handlePrintCoverNote = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!qrRequestId || printingCoverNote) return;

    setShowCoverNote(false);
    setPrintingCoverNote(true);
    const r = await printCoverNotePdf(qrRequestId, flow, detail.fileRef || undefined);
    setPrintingCoverNote(false);

    if (!r.success) {
      setFlashToast({ message: r.message ?? t('tasks.details.coverNotePdfFailed'), variant: 'error' });
    }
  };

  const submitLabel =
    detail.workflowActionMode === 'complete' ? t('tasks.details.complete') : t('tasks.details.submit');
  const wfaActions: { id: WfaAction; label: string }[] = useMemo(() => {
    const ttype = taskTypeKey(detail.taskType);
    let actions: { id: WfaAction; label: string }[];

    if (ttype === 'subsidiary') {
      actions = [
        { id: 'forward', label: t('tasks.details.forward') },
        { id: 'revert', label: t('tasks.details.revert') },
      ];
    } else if (detail.isReceivedViaForward) {
      actions = [{ id: 'revert', label: t('tasks.details.revert') }];
      if (detail.workflowActionMode !== 'complete' && detail.nextStageIsClosure) {
        actions.push({ id: 'closure', label: t('tasks.details.closure') });
      }
    } else if (ttype === 'review') {
      actions = [
        { id: 'forward', label: t('tasks.details.forward') },
        { id: 'revert', label: t('tasks.details.revert') },
      ];
    } else if (detail.workflowActionMode === 'complete') {
      actions = [
        { id: 'forward', label: t('tasks.details.forward') },
        { id: 'revert', label: t('tasks.details.revert') },
        // UAT-45: { id: 'reject', label: t('tasks.details.reject') },
      ];
    } else {
      actions = [
        { id: 'forward', label: t('tasks.details.forward') },
        { id: 'revert', label: t('tasks.details.revert') },
        // UAT-45: { id: 'reject', label: t('tasks.details.reject') },
      ];
      if (detail.nextStageIsClosure) {
        actions.push({ id: 'closure', label: t('tasks.details.closure') });
      }
    }

    if (allocationOfficerUser || roleCode === 'STATE_ATTORNEY') {
      return actions.filter((a) => a.id === 'forward' || a.id === 'revert');
    }
    return actions;
  }, [
    allocationOfficerUser,
    roleCode,
    detail.workflowActionMode,
    detail.nextStageIsClosure,
    detail.taskType,
    detail.isReceivedViaForward,
    t,
  ]);

  const wfaStageExcluded = isExcludedWorkflowActionStage(detail.currentStage);
  const ttype = taskTypeKey(detail.taskType);
  const isSubsidiaryTask = ttype === 'subsidiary';
  const isReviewTask = ttype === 'review';
  const receivedViaForward = detail.isReceivedViaForward === true;
  const wfaAllowedForTaskType = !isSubsidiaryTask;
  const showWfaSubmit =
    !isSubsidiaryTask &&
    (!isReviewTask || receivedViaForward) &&
    !allocationOfficerUser &&
    roleCode !== 'STATE_ATTORNEY';
  const showWorkflowActionsCard = isSubsidiaryTask
    ? detail.showWorkflowActions && !viewMode && !allocationOfficerUser
    : detail.showWorkflowActions &&
      showCaseWorkflowActions &&
      !allocationOfficerUser &&
      !viewMode &&
      !wfaStageExcluded &&
      wfaAllowedForTaskType;

  const blockingInfoMessage = t('tasks.details.blockingSubTasks');

  useEffect(() => {
    if (!showWorkflowActionsCard) return;
    if (!workflowActionsEnabled) return;
    // Only main tasks (REGULAR/REVIEW) can have blocking subsidiary tasks.
    if (taskTypeKey(detail.taskType) === 'subsidiary') return;
    const id = (userTaskId ?? '').trim();
    if (!id) return;

    const controller = new AbortController();
    void (async () => {
      setBlockingLoading(true);
      const r = await getWorkflowTaskSubsidiaryStatus({ taskId: id, signal: controller.signal });
      if (controller.signal.aborted) return;
      setBlockingLoading(false);
      if (!r.success) return;
      setBlockingSubTasks(Boolean(r.data.hasBlockingSubTasks));
    })();

    return () => controller.abort();
  }, [showWorkflowActionsCard, workflowActionsEnabled, detail.taskType, userTaskId]);

  const refreshBlockingSubsidiaryTasks = async (): Promise<boolean> => {
    if (!showWorkflowActionsCard) return false;
    if (!workflowActionsEnabled) return false;
    if (taskTypeKey(detail.taskType) === 'subsidiary') return false;
    const id = (userTaskId ?? '').trim();
    if (!id) return false;

    const controller = new AbortController();
    setBlockingLoading(true);
    try {
      const r = await getWorkflowTaskSubsidiaryStatus({ taskId: id, signal: controller.signal });
      if (!r.success) return blockingSubTasks;
      const next = Boolean(r.data.hasBlockingSubTasks);
      setBlockingSubTasks(next);
      return next;
    } catch {
      return blockingSubTasks;
    } finally {
      setBlockingLoading(false);
      controller.abort();
    }
  };

  if (loading) {
    return (
      <>
        <TaskCaseDetailsBreadcrumb returnState={breadcrumbReturn} />
        <div className="pg-title">{t('tasks.details.title')}</div>
        <p className="tcd-loading" style={{ padding: '24px 0', color: 'var(--t2)' }}>
          {t('tasks.details.loading')}
        </p>
      </>
    );
  }

  if (error) {
    return (
      <>
        <TaskCaseDetailsBreadcrumb returnState={breadcrumbReturn} />
        <div className="pg-title">{t('tasks.details.title')}</div>
        <p className="tcd-error" style={{ padding: '24px 0', color: 'var(--red)' }} role="alert">
          {error}
        </p>
      </>
    );
  }

  // EM-1: Start button removed — tasks are available for processing immediately after allocation.
  // const handleStart = async () => {
  //   if (!canStart || starting) return;
  //   if (!caseRequestId || !userTaskId) {
  //     setFlashToast({ message: t('tasks.details.startMissingIds'), variant: 'error' });
  //     return;
  //   }
  //
  //   setStarting(true);
  //   const r = await startUserTask({ caseRequestId, workflowInstanceStageUserTaskId: userTaskId });
  //   setStarting(false);
  //
  //   if (!r.success) {
  //     setFlashToast({ message: r.message ?? t('tasks.details.startFailed'), variant: 'error' });
  //     return;
  //   }
  //
  //   reload();
  //   setFlashToast({ message: t('tasks.details.startSuccess'), variant: 'success' });
  // };

  const handleSubmitClick = async () => {
    if (!workflowActionsEnabled || submitValidating) return;
    const latestBlocking = await refreshBlockingSubsidiaryTasks();
    if (latestBlocking) {
      setSubmitOpen(true);
      return;
    }

    const effectiveCaseRequestId = isCaseRequestUuid(resolvedCaseRequestId)
      ? resolvedCaseRequestId
      : caseRequestId;

    if (!isCaseRequestUuid(effectiveCaseRequestId) || !isCaseRequestUuid(userTaskId)) {
      setSubmitOpen(true);
      return;
    }

    if (detail.workflowActionMode === 'complete') {
      setSubmitOpen(true);
      return;
    }

    setSubmitValidating(true);
    const r = await validateSubmitWorkflowTask({
      caseRequestId: effectiveCaseRequestId,
      workflowInstanceStageUserTaskId: userTaskId,
    });
    setSubmitValidating(false);

    if (!r.success) {
      setFlashToast({ message: r.message, variant: 'error' });
      return;
    }

    setSubmitOpen(true);
  };

  const handleClosureClick = async () => {
    if (!workflowActionsEnabled || closureValidating) return;
    await refreshBlockingSubsidiaryTasks();

    const id = (userTaskId ?? '').trim();
    if (!isCaseRequestUuid(id)) {
      setClosureExistingAssignment(undefined);
      setClosureOpen(true);
      return;
    }

    setClosureValidating(true);
    const r = await validateClosureApproverClick({ taskId: id });
    setClosureValidating(false);

    if (!r.success) {
      setFlashToast({ message: r.message || t('tasks.details.closureValidateFailed'), variant: 'error' });
      return;
    }

    setClosureExistingAssignment(r.data.existingAssignment);
    setClosureOpen(true);
  };

  return (
    <>
      <TaskCaseDetailsBreadcrumb returnState={breadcrumbReturn} />

      <div className="tcd-page-head">
        <div className="pg-title">{t('tasks.details.title')}</div>
        {/* EM-1: Start button removed — allocated tasks move to "In Progress" without officer action.
        {!readOnly &&
        showCaseWorkflowActions &&
        !has('ALLOCATION_OFFICER_VIEW') &&
        !wfaStageExcluded &&
        taskTypeKey(detail.taskType) !== 'subsidiary' &&
        taskTypeKey(detail.taskType) !== 'review' &&
        !workflowAtLastStage ? (
          <button
            type="button"
            className="btn-started"
            onClick={() => void handleStart()}
            aria-pressed={!canStart}
            disabled={!canStart || starting}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            {canStart ? (starting ? t('tasks.details.starting') : t('tasks.details.start')) : t('tasks.details.started')}
          </button>
        ) : null}
        */}
      </div>

      <CriminalCoverNoteModal
        open={showCoverNote && flow === 'criminal'}
        caseRequestId={qrRequestId}
        fileRefFallback={detail.fileRef}
        onClose={() => setShowCoverNote(false)}
      />
      <CommonCoverNoteModal
        isOpen={showCoverNote && usesCommonCoverNote(flow)}
        caseRequestId={qrRequestId}
        fileRefFallback={detail.fileRef}
        onClose={() => setShowCoverNote(false)}
      />
      <CriminalAppellateCoverNoteModal
        isOpen={showCoverNote && usesCriminalAppellateCoverNote(flow)}
        caseRequestId={qrRequestId}
        fileRefFallback={detail.fileRef}
        onClose={() => setShowCoverNote(false)}
      />
      <RightToInformationCoverNoteModal
        isOpen={showCoverNote && flow === 'right-to-information'}
        caseRequestId={qrRequestId}
        fileRefFallback={detail.fileRef}
        coverNoteFallback={rtiCoverNoteFallback}
        onClose={() => setShowCoverNote(false)}
      />

      <div className="tcd-meta">
        <div className="tm">
          <label>{t('tasks.details.fileReferenceNumber')}</label>
          <p>{detail.fileRef}</p>
        </div>
        <div className="tm-div" />
        <div className="tm">
          <label>{t('tasks.shared.fileCategory')}</label>
          <p>{detail.fileCategory}</p>
        </div>
        <div className="tm-div" />
        <div className="tm">
          <label>{t('tasks.shared.subFileCategory')}</label>
          <p>{detail.subFileCategory}</p>
        </div>
        <div className="tm-div" />
        <div className="tm tm-grow">
          <label>{t('tasks.details.currentStage')}</label>
          <p>{detail.currentStage}</p>
        </div>
        <div className="tm-div" />
        <div className="tm">
          <span className="tm-upper">{t('tasks.shared.taskStatus')}</span>
          <span className={`tcd-status-pill ${detail.taskStatusClass}`}>{translateTaskStatus(detail.taskStatus)}</span>
        </div>
        <div className="tm-div" />
        <div className="tm">
          <span className="tm-upper">{t('tasks.details.physicalFileCustody')}</span>
          <span className={`tcd-custody-pill ${detail.custodyClass}`}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            {translateCustodyStatus(detail.custodyStatus)}
          </span>
        </div>
        {detail.linkedFileRef ? (
          <>
            <div className="tm-div" />
            <div className="tm tm-link">
              <label>{t('tasks.details.linkedFileRef')}</label>
              <p>
                <Link to={taskDetailsPath(detail.linkedFileRef)}>{detail.linkedFileRef}</Link>
              </p>
            </div>
          </>
        ) : null}
      </div>

      <div className={`tcd-body${detail.showWorkflow ? '' : ' no-workflow'}`}>
        {detail.showWorkflow ? (
          <div className="wf-card">
            <div className="wf-title">{t('tasks.details.workflowProgress')}</div>
            {detail.workflowSteps.map((s, i) => (
              <Fragment key={`${s.title}-${i}`}>
                {renderWorkflowStep(s, i === detail.workflowSteps.length - 1)}
              </Fragment>
            ))}
            <div className="wf-leg">
              <div className="wf-li">
                <div className="wf-ld" style={{ background: 'var(--sb)' }} />
                {WORKFLOW_LEGEND_COMPLETED}
              </div>
              <div className="wf-li">
                <div className="wf-ld" style={{ background: 'var(--red)' }} />
                {WORKFLOW_LEGEND_CURRENT}
              </div>
              <div className="wf-li">
                <div className="wf-ld" style={{ background: '#D5D2CB' }} />
                {WORKFLOW_LEGEND_PENDING}
              </div>
            </div>
          </div>
        ) : null}

        <div className="tab-card tcd-tab-card">
          <div className="tab-bar">
            {visibleTabs.map((tabItem) => (
              <button
                key={tabItem.id}
                type="button"
                className={`tab-b ${activeTab === tabItem.id ? 'on' : ''}`}
                onClick={() => setTab(tabItem.id)}
              >
                {tabItem.label}
              </button>
            ))}
          </div>

          <div className="tab-body">
            {activeTab === 'cd' ? (
              viewPayloadLoading ? (
                <p className="tcd-loading" style={{ color: 'var(--t2)' }}>
                  {t('tasks.details.loading')}
                </p>
              ) : caseDetailsError ? (
                <p className="tcd-error" style={{ color: 'var(--red)' }} role="alert">
                  {t('tasks.details.caseDetailsLoadFailed')}
                </p>
              ) : viewPayload ? (
                <ViewRequestCaseDetailsRouter
                  payload={viewPayload}
                  caseRequestId={resolvedCaseRequestId || caseRequestId}
                  hideUploadDocumentsStep
                />
              ) : (
                <p className="tcd-loading" style={{ color: 'var(--t2)' }}>
                  {t('viewRequest.notFound')}
                </p>
              )
            ) : null}

            {activeTab === 'cu' && flow !== 'unknown' ? (
              <TaskCaseUpdateTabRouter
                flow={flow}
                caseRequestId={resolvedCaseRequestId || caseRequestId}
                recentSubmittedAt={detail.recentSubmittedAt}
                readOnly={tabsReadOnly}
                onCancelConfirmed={() => setTab('cd')}
              />
            ) : null}

            {activeTab === 'docs' ? (
              <TaskCaseDocumentsTab
                caseRequestId={resolvedCaseRequestId}
                readOnly={tabsReadOnly}
                flow={flow}
              />
            ) : null}

            {activeTab === 'comm' ? (
              <TaskCaseCommunicationsTab
                caseRequestId={
                  isCaseRequestUuid(resolvedCaseRequestId) ? resolvedCaseRequestId : caseRequestId
                }
                taskStatus={detail.taskStatus}
                workflowInstanceStageUserTaskId={userTaskId}
                isRtiCase={flow === 'right-to-information'}
                readOnly={readOnly}
                composeEnabled={communicationsComposeEnabled}
                holdResumeEnabled={communicationsHoldResumeEnabled}
                onTaskStatusChange={() => void reload()}
              />
            ) : null}

            {activeTab === 'min' ? (
              <TaskCaseMinutesTab
                caseRequestId={caseRequestId}
                readOnly={tabsReadOnly}
              />
            ) : null}

            {activeTab === 'ltr' ? (
              <TaskCaseDraftLetterTab
                caseRequestId={caseRequestId}
                readOnly={tabsReadOnly}
              />
            ) : null}

            {activeTab === 'st' ? (
              <TaskCaseSubTasksTab
                caseRequestId={caseRequestId}
                parentRef={detail.fileRef}
                fileCategoryId={detail.fileCategoryId ?? ''}
                subFileCategoryId={detail.subFileCategoryId ?? ''}
                taskType={detail.taskType}
                isReceivedViaForward={detail.isReceivedViaForward}
                workflowStage={detail.currentStage}
                readOnly={subTasksTabReadOnly}
              />
            ) : null}

            {activeTab === 'fml' ? (
              <>
                <div className="log-title">{t('tasks.details.fileMovementTitle')}</div>
                {detail.fileMovement.length ? (
                  <table className="log-tbl">
                    <thead>
                      <tr>
                        <th style={{ whiteSpace: 'nowrap' }}>{t('tasks.details.dateTime')}</th>
                        <th>{t('tasks.details.event')}</th>
                        <th>{t('tasks.details.fileCustody')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.fileMovement.map((r) => (
                        <tr key={`${r.date}-${r.time}-${r.note}`}>
                          <td>
                            <div className="dt-main">{r.date}</div>
                            <div className="dt-sub">{r.time}</div>
                          </td>
                          <td>
                            <span className={`ev-pill ${fmlEvClass(r.event)}`}>
                              {r.event === 'qrin' ? t('tasks.details.qrCheckIn') : t('tasks.details.qrCheckOut')}
                            </span>
                          </td>
                          <td>
                            <div className="fml-custody">
                              <div className="fml-name">{r.name}</div>
                              <div className="fml-role">{r.role}</div>
                              <div className="fml-note">{r.note}</div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p style={{ padding: '14px 0', color: 'var(--t2)' }}>{t('tasks.details.noFileMovement')}</p>
                )}
              </>
            ) : null}

            {activeTab === 'al' ? (
              <TaskCaseAuditLogTab caseRequestId={caseRequestId} />
            ) : null}

            {activeTab === 'inv' ? (
              <TaskCaseInvolvementTab caseRequestId={caseRequestId} />
            ) : null}
          </div>
        </div>

        <div className="rp">
          <div className="qr-panel tcd-qr-panel">
            <div className="qr-img-w">
              {coverNoteLoading ? (
                <div className="tcd-qr-placeholder" aria-busy="true">
                  {t('tasks.details.loadingQr')}
                </div>
              ) : qrSrc ? (
                <div style={{ background: '#fff', padding: 8, borderRadius: 8 }}>
                  <img src={qrSrc} alt={t('tasks.details.qrAlt')} width={120} height={120} />
                </div>
              ) : (
                <div className="tcd-qr-placeholder">{t('tasks.details.qrUnavailable')}</div>
              )}
            </div>
            {/* <p className="qr-tip">
              Present this QR code together with the cover note when delivering the physical file to AGD.
            </p> */}
            <button
              className="qr-ab"
              type="button"
              onClick={() => setShowCoverNote(true)}
              disabled={!qrRequestId || coverNoteLoading}
            >
              {t('tasks.details.viewCoverNote')}
            </button>
            <button
              className="qr-ab tcd-print-btn"
              type="button"
              onClick={(e) => void handlePrintCoverNote(e)}
              disabled={!qrRequestId || coverNoteLoading || printingCoverNote}
            >
              {printingCoverNote ? t('tasks.details.preparing') : t('tasks.details.printCoverNote')}
            </button>
          </div>

          {showWorkflowActionsCard ? (
            <div className="wfa-card">
              <div className="wfa-title">{t('tasks.details.workflowActions')}</div>
              <p className="wfa-hint">
                {isReviewTask && !receivedViaForward
                  ? t('tasks.details.reviewForwardHint')
                  : detail.workflowActionMode === 'complete'
                    ? t('tasks.details.completeHint')
                    : t('tasks.details.submitHint', { submitLabel })}
              </p>
              <div className="wfa-stack">
                {wfaActions.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className={`wfa-stack-btn${wfa === a.id ? ' on' : ''}`}
                    onClick={() => {
                      if (!workflowActionsEnabled) return;
                      setWfa((cur) => (cur === a.id ? '' : a.id));
                      if (a.id === 'forward') setForwardOpen(true);
                      if (a.id === 'revert') setRevertOpen(true);
                      // UAT-45: if (a.id === 'reject') setRejectOpen(true);
                      if (a.id === 'closure') void handleClosureClick();
                    }}
                    disabled={!workflowActionsEnabled}
                  >
                    {a.label}
                  </button>
                ))}
                {showWfaSubmit ? (
                  <button
                    type="button"
                    className="wfa-submit"
                    onClick={() => void handleSubmitClick()}
                    disabled={!workflowActionsEnabled || submitValidating}
                  >
                    {submitValidating || blockingLoading ? t('tasks.details.checking') : submitLabel}
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <ForwardTaskModal
        open={forwardOpen}
        workflowInstanceStageUserTaskId={userTaskId}
        fileCategoryId={detail.fileCategoryId ?? ''}
        subFileCategoryId={detail.subFileCategoryId ?? ''}
        onClose={() => {
          setForwardOpen(false);
          setWfa((cur) => (cur === 'forward' ? '' : cur));
        }}
        onSuccess={() => {
          setForwardOpen(false);
          setWfa((cur) => (cur === 'forward' ? '' : cur));
          nav(forwardReturnPath);
        }}
      />

      <SubmitTaskModal
        open={submitOpen}
        mode={detail.workflowActionMode}
        caseRequestId={
          isCaseRequestUuid(resolvedCaseRequestId) ? resolvedCaseRequestId : caseRequestId
        }
        workflowInstanceStageUserTaskId={userTaskId}
        blockedBySubsidiaryTasks={blockingSubTasks}
        blockingInfoMessage={blockingInfoMessage}
        onClose={() => setSubmitOpen(false)}
        onSuccess={() => {
          setSubmitOpen(false);
          nav(breadcrumbReturn.returnTo);
        }}
      />

      <SelectUserForTaskClosureModal
        open={closureOpen}
        workflowInstanceStageUserTaskId={userTaskId}
        blockedBySubsidiaryTasks={blockingSubTasks}
        blockingInfoMessage={blockingInfoMessage}
        initialAssignment={closureExistingAssignment}
        onClose={() => {
          setClosureOpen(false);
          setClosureExistingAssignment(undefined);
          setWfa((cur) => (cur === 'closure' ? '' : cur));
        }}
        onSuccess={() => {
          setClosureOpen(false);
          setClosureExistingAssignment(undefined);
          setWfa((cur) => (cur === 'closure' ? '' : cur));
          reload();
        }}
      />

      {/* UAT-45: Reject action removed — rejections now route through closure workflow.
      <RejectTaskModal
        open={rejectOpen}
        caseRequestId={
          isCaseRequestUuid(resolvedCaseRequestId) ? resolvedCaseRequestId : caseRequestId
        }
        workflowInstanceStageUserTaskId={userTaskId}
        onClose={() => {
          setRejectOpen(false);
          setWfa((cur) => (cur === 'reject' ? '' : cur));
        }}
        onSuccess={() => {
          setRejectOpen(false);
          setWfa((cur) => (cur === 'reject' ? '' : cur));
          nav('/tasks');
        }}
      />
      */}

      <RevertTaskModal
        open={revertOpen}
        caseRequestId={
          isCaseRequestUuid(resolvedCaseRequestId) ? resolvedCaseRequestId : caseRequestId
        }
        workflowInstanceStageUserTaskId={userTaskId}
        fileCategoryId={detail.fileCategoryId ?? ''}
        subFileCategoryId={detail.subFileCategoryId ?? ''}
        isSubsidiaryTask={isSubsidiaryTask}
        currentStage={detail.currentStage}
        taskCreatedById={detail.taskCreatedById}
        taskCreatedByName={detail.taskCreatedByName}
        onClose={() => {
          setRevertOpen(false);
          setWfa((cur) => (cur === 'revert' ? '' : cur));
        }}
        onSuccess={() => {
          setRevertOpen(false);
          setWfa((cur) => (cur === 'revert' ? '' : cur));
          nav(breadcrumbReturn.returnTo);
        }}
      />
    </>
  );
}
